import React, { useState, useEffect } from 'react';
import { Drawer, Grid, FormControl, InputLabel, Select, MenuItem, Button } from '@mui/material';
import { SALES_GROUP, CreditLineStatusEnum } from '@trnsact/trnsact-shared-types';
import SaveIcon from '@mui/icons-material/Save';
import LenderTaskForm from 'components/LenderTaskForm';
import EmailActionForm from './EmailActionForm';
import _ from 'lodash';
import { useWorkflowStyles } from './styles';
import { formatConstantNames } from './utils';
import {
  VOStatus,
  stageOptions,
  creditLineStatusOptions,
  WorkflowEventType,
  workflowEventOptions,
  WorkflowActionType,
  workflowActionOptions,
} from './constants';

interface WorkflowDrawerProps {
  open: boolean;
  editingBox: 'Event' | 'Condition' | 'Action' | null;
  selectedEvent: WorkflowEventType;
  selectedConditions: (VOStatus | CreditLineStatusEnum)[];
  selectedAction: WorkflowActionType;
  selectedActionConfiguration: any;
  accountId: string;
  accountName: string;
  onClose: () => void;
  onSave: () => void;
  onEventChange: (value: WorkflowEventType) => void;
  onConditionsChange: (value: (VOStatus | CreditLineStatusEnum)[]) => void;
  onActionChange: (value: WorkflowActionType) => void;
  onSaveTask: (payload: any) => void;
  onSaveEmail: (payload: any) => void;
}

const WorkflowDrawer: React.FC<WorkflowDrawerProps> = ({
  open,
  editingBox,
  selectedEvent,
  selectedConditions,
  selectedAction,
  selectedActionConfiguration,
  accountId,
  accountName,
  onClose,
  onSave,
  onEventChange,
  onConditionsChange,
  onActionChange,
  onSaveTask,
  onSaveEmail,
}) => {
  const classes = useWorkflowStyles();
  const [emailFormData, setEmailFormData] = useState<any>(null);

  // Determine which condition options to show based on selected event
  const getConditionOptions = () => {
    switch (selectedEvent) {
      case WorkflowEventType.VENDOR_OPPORTUNITY_UPDATED:
        return stageOptions;
      case WorkflowEventType.CREDIT_LINE_STATUS_UPDATED:
        return creditLineStatusOptions;
      default:
        return [];
    }
  };

  const handleEmailFormChange = (data: any) => {
    setEmailFormData(data);
  };

  const handleSaveEmailAction = () => {
    if (emailFormData) {
      onSaveEmail(emailFormData);
      onSave();
    }
  };

  return (
    <Drawer anchor="right" variant="temporary" open={open} onClose={onClose}>
      <Grid container className={classes.drawerContent}>
        <Grid item xs={12}>
          <div>
            {editingBox === 'Event' && (
              <>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="event-select-label">Event</InputLabel>
                  <Select
                    labelId="event-select-label"
                    value={selectedEvent}
                    onChange={e => onEventChange(e.target.value as WorkflowEventType)}
                  >
                    {workflowEventOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button onClick={onSave} color="primary" variant="outlined" startIcon={<SaveIcon />}>
                  Save Event
                </Button>
              </>
            )}
            {editingBox === 'Condition' && (
              <>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="condition-select-label">Condition</InputLabel>
                  <Select
                    labelId="condition-select-label"
                    multiple
                    value={selectedConditions}
                    onChange={e => onConditionsChange(e.target.value as (VOStatus | CreditLineStatusEnum)[])}
                  >
                    {getConditionOptions().map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button onClick={onSave} color="primary" variant="outlined" startIcon={<SaveIcon />}>
                  Save Condition
                </Button>
              </>
            )}
            {editingBox === 'Action' && (
              <Grid container spacing={5}>
                <Grid item xs={12}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="action-select-label">Action</InputLabel>
                    <Select
                      labelId="action-select-label"
                      value={selectedAction}
                      onChange={e => onActionChange(e.target.value as WorkflowActionType)}
                    >
                      {workflowActionOptions.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {selectedAction === WorkflowActionType.CREATE_TASK && (
                  <Grid item xs={12}>
                    <LenderTaskForm
                      tasksData={
                        _.get(selectedActionConfiguration, 'CREATE_TASK')
                          ? [_.get(selectedActionConfiguration, 'CREATE_TASK')]
                          : []
                      }
                      taskId={!_.isEmpty(_.get(selectedActionConfiguration, 'CREATE_TASK', [])) ? '-1' : null}
                      drawerContained={true}
                      handleAddTask={onSaveTask}
                      handleClose={onClose}
                      renderInWorkflowConstructor={true}
                      dynamicRecipients={[
                        {
                          id: 'applicant',
                          ownerPgId: 'will_be_changed_by_backend',
                          firstName: 'Primary',
                          fullName: 'Primary Contact',
                          lastName: 'Contact',
                          email: 'will_be_changed_by_backend',
                          company: 'Customer',
                          role: 'POC',
                          isApplicant: true,
                          category: 'Customer',
                        },
                        {
                          id: 'sales_mgr',
                          firstName: 'Sales',
                          fullName: SALES_GROUP.sales_mgr,
                          lastName: 'Manager',
                          email: 'will_be_changed_by_backend',
                          company: accountName,
                          role: SALES_GROUP.sales_mgr,
                          isApplicant: false,
                          category: 'Associated User By Role',
                        },
                        {
                          id: 'sales_rep',
                          firstName: 'Sales',
                          fullName: SALES_GROUP.sales_rep,
                          lastName: 'Representative',
                          email: 'will_be_changed_by_backend',
                          company: accountName,
                          role: SALES_GROUP.sales_rep,
                          isApplicant: false,
                          category: 'Associated User By Role',
                        },
                        {
                          id: 'finance_manager',
                          firstName: 'Finance',
                          fullName: SALES_GROUP.finance_manager,
                          lastName: 'Manager',
                          email: 'will_be_changed_by_backend',
                          company: accountName,
                          role: SALES_GROUP.finance_manager,
                          isApplicant: false,
                          category: 'Associated User By Role',
                        },
                      ]}
                    />
                  </Grid>
                )}

                {selectedAction === WorkflowActionType.SEND_EMAIL && (
                  <Grid item xs={12}>
                    <EmailActionForm
                      accountId={accountId}
                      initialData={_.get(selectedActionConfiguration, 'SEND_EMAIL')}
                      onChange={handleEmailFormChange}
                    />
                    <Button
                      onClick={handleSaveEmailAction}
                      color="primary"
                      variant="outlined"
                      startIcon={<SaveIcon />}
                      style={{ marginTop: 16 }}
                      disabled={!emailFormData?.templateId}
                    >
                      Save Email Action
                    </Button>
                  </Grid>
                )}
              </Grid>
            )}
          </div>
        </Grid>
      </Grid>
    </Drawer>
  );
};

export default WorkflowDrawer;
